import React, { useContext, useEffect, useRef } from 'react';
import ReactToPrint, { PrintContextConsumer } from 'react-to-print';
import { Snackbar, useMediaQuery } from '@material-ui/core';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import {
  Watermark,
  WidgetHeader,
  AgGridWidgetWrapper,
  pageStyle,
} from '@mummssoftware/common-ui';
import { CPPGlobalContext } from '@mummssoftware/global-context/CPPGlobalContext';
import { Contents } from '@mummssoftware/utils/constants';
import { isAgencyEnabled } from '@mummssoftware/utils/utils';
import { SubmitPlan } from './SubmitPlan';
import { Context } from '../../context';
import { CPPContext } from '../Context/CPPContext';
import { CarePlanTable } from './CarePlanTable';
import { CarePlanMenu } from './CarePlanMenu';
import { CarePlanProblemsPrint } from '../Print';
import { Container } from '../../components/layout/WrapperDiv';
import defaults from '../../translations/en.json';

export declare const RouteProps: import('react-router-dom').RouteComponentProps<{
  patientNumber: string;
}>;

type CarePlanProblemsProps = {
  currentUser: mumms.User;
  online: boolean;
  patient?: mumms.patient;
  patientCarePlanProblem: mumms.patientCarePlanProblem[];
  appTitle?: string;
  view?: string | null;
  smallWidget?: boolean;
} & typeof RouteProps;

export const CarePlanProblems = ({ smallWidget }: CarePlanProblemsProps) => {
  const {
    errorInfo,
    clearError,
    appTitle,
    patientCarePlanProblem,
    allCarePlanProblems,
    onClick,
    onRemove,
    doNotShow,
    printConfig,
    closePlan,
    currentUser,
    startLoadingTime,
    readOnly,
    onWidgetReady,
  } = useContext(Context);
  const { cppForm, submitPlanActive } = useContext(CPPContext);
  const { answeredSubmittedCPPGlobal } = useContext(CPPGlobalContext);

  const printRef = useRef<HTMLDivElement | null>(null);

  const isNarrowViewport = useMediaQuery('(max-width:600px)', {
    noSsr: true,
  });
  /** In expanded view with the submit strip, the care plan name crowds DISCARD / SUBMIT on a narrow screen; hide the title only there. */
  const hideHeaderCarePlanTitle =
    isNarrowViewport && !smallWidget && Boolean(cppForm);
  const widgetAppTitle = hideHeaderCarePlanTitle
    ? ''
    : cppForm?.careplanproblemname || appTitle || defaults.title;

  const toFillCarePlanProblems =
    allCarePlanProblems && allCarePlanProblems.length
      ? allCarePlanProblems.filter(
          (plan: mumms.carePlanProblem) => plan.isinuse === 'false',
        )
      : [];

  const Alert = (props: AlertProps) => (
    <MuiAlert elevation={6} variant="filled" {...props} />
  );

  // expanded Care Plan Problem View printConfig and Notes available
  const expandedCPPprintConfig = {
    view: Contents,
    notesMode: 'dateRange' as mumms.notesMode,
  };
  const showPrint = !smallWidget && !!patientCarePlanProblem.length;

  const handleOnClick = async () => {
    if (onClick) {
      onClick();
    }
  };

  useEffect(() => {
    const loadingDuration = (Date.now() - startLoadingTime) / 1000;
    // callback to alert dashboard the view is ready
    onWidgetReady && onWidgetReady('CarePlanProblems' as mumms.ComponentName);
    console.log('## CARE PLAN PROBLEMS WIDGET END ##', loadingDuration, 's');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 
  setTimeOut to avoid CPP Context Leak issue
  Only close when read only view API call finished
   */
  useEffect(() => {
    if (onClick && submitPlanActive && closePlan) {
      setTimeout(() => {
        onClick();
      }, 2000);
    }
  }, [submitPlanActive, closePlan]);

  const disabled = !!submitPlanActive;

  // show watermark based on agencies
  const enabledCPPBins: string = window.env?.enabledCPPBins as string;
  const isSelectedAgency = isAgencyEnabled(enabledCPPBins, currentUser);
  // answered submitted CPP for printing from CPP print icon
  // patientCarePlanProblem is a fallback not expected to be used
  const printCPP = answeredSubmittedCPPGlobal || patientCarePlanProblem;
  const showAddCpp =
    !!toFillCarePlanProblems.length && !smallWidget && !readOnly;

  return (
    <Container id="care-pan-container">
      {isSelectedAgency ? (
        <Watermark readOnly={false} smallWidget={smallWidget} />
      ) : null}
      <Snackbar
        open={!!errorInfo}
        autoHideDuration={3000}
        onClose={() => clearError()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert onClose={() => clearError()} severity={errorInfo?.severity}>
          {errorInfo?.text}
        </Alert>
      </Snackbar>
      <ReactToPrint content={() => printRef.current} pageStyle={pageStyle}>
        <PrintContextConsumer>
          {({ handlePrint }) => (
            <WidgetHeader
              appTitle={widgetAppTitle}
              versionInfo={`${defaults.title} v${process.env.REACT_APP_CARE_PLAN_PROBLEMS_VERSION}, SmartChart v${process.env.REACT_APP_VERSION}`}
              smallWidget={smallWidget}
              onClick={handleOnClick}
              onRemove={onRemove}
              doNotShow={doNotShow}
              disabled={disabled}
            >
              <SubmitPlan />
              {showAddCpp && (
                <CarePlanMenu newCarePlans={toFillCarePlanProblems} />
              )}
            </WidgetHeader>
          )}
        </PrintContextConsumer>
      </ReactToPrint>

      <AgGridWidgetWrapper
        id="care-plan-problems-body"
        smallWidget={smallWidget}
      >
        <CarePlanTable />
      </AgGridWidgetWrapper>
      {/* viewModePrint from CARE PLAN PROBLEM print icon */}
      {/* // todo REMOVE */}
      {/* {!printConfig && (
        <div
          style={{
            display: 'none',
          }}
        >
          <CarePlanProblemsPrint
            patientCarePlanProblem={printCPP}
            printConfig={expandedCPPprintConfig}
            ref={printRef}
          />
        </div>
      )} */}
    </Container>
  );
};

export default CarePlanProblems;
